import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { useCart } from "@/context/CartContext";
import { ShopHeader } from "@/components/ShopHeader";
import { ShopFooter } from "@/components/ShopFooter";
import { CategorySidebar } from "@/components/CategorySidebar";
import { StoreNav } from "@/components/StoreNav";
import { Package, Plus, ArrowRight, CaretLeft, CaretRight } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const P = "/legacy/promo/";
const BANNERS = [P + "b1.jpg", P + "b2_5orf-qj.jpg", P + "s1.jpg", P + "s2.jpg", P + "s3.jpg", P + "s4.jpg", P + "s5.jpg"];
const PROMO_LEFT = "/legacy/promo/promo_paper.jpg";
const PROMO_RIGHT = "/legacy/promo/promo_hand.jpg";

const BRANDS = [
  "/legacy/brands/3M-logo-079FB52BC8-seeklogo.com_-1.png",
  "/legacy/brands/Cascades_Inc_Logo.svg_-1.png",
  "/legacy/brands/clorox-Logo.png",
  "/legacy/brands/Product-GOJO-Brand.png",
  "/legacy/brands/Purell-logo-1.png",
  "/legacy/brands/Rubbermaid.svg_.png",
  "/legacy/brands/san-jamar.png",
  "/legacy/brands/Tork-Logo-700x394.png",
  "/legacy/brands/vileda.png",
  "/legacy/brands/lysol-Logo.png",
  "/legacy/brands/avmor.png",
  "/legacy/brands/continetal.png",
];

const TopNav = () => {
  return <StoreNav active="home" />;
};

const Carousel = ({ banners, lang }) => {
  const slides = (banners && banners.length)
    ? banners.map((b) => ({ img: (lang === "en" ? (b.image_en || b.image_fr) : (b.image_fr || b.image_en)), link: b.link_url, blank: b.open_new_window }))
    : BANNERS.map((u) => ({ img: u, link: "", blank: false }));
  const [i, setI] = useState(0);
  const n = slides.length;
  const navigate = useNavigate();
  useEffect(() => { setI(0); }, [n]);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % n), 4500);
    return () => clearInterval(id);
  }, [n]);
  const openLink = (s) => {
    if (!s.link) return;
    if (s.blank || /^https?:\/\//i.test(s.link)) window.open(s.link, s.blank ? "_blank" : "_self");
    else navigate(s.link);
  };
  return (
    <div className="relative mt-2 flex-1 overflow-hidden border border-border bg-white" data-testid="banner-carousel">
      <div className="flex h-full min-h-[320px] items-center justify-center md:min-h-[460px]">
        {slides.map((s, idx) => (
          <img key={idx} src={s.img} alt="" onClick={() => openLink(s)}
            className={`absolute max-h-full w-full object-contain transition-opacity duration-500 ${idx === i ? "opacity-100" : "opacity-0"} ${s.link ? "cursor-pointer" : ""}`}
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
        ))}
      </div>
      <button onClick={() => setI((i - 1 + n) % n)} className="absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white hover:bg-black/50"><CaretLeft size={18} weight="bold" /></button>
      <button onClick={() => setI((i + 1) % n)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white hover:bg-black/50"><CaretRight size={18} weight="bold" /></button>
      <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
        {slides.map((_, idx) => (
          <button key={idx} onClick={() => setI(idx)}
            className={`h-6 w-6 rounded-full text-[11px] font-bold ${idx === i ? "bg-primary text-white" : "bg-white/70 text-[#2e3a47]"}`}>{idx + 1}</button>
        ))}
      </div>
    </div>
  );
};

// Bloc promo (papier / soin des mains) : photo de fond + texte bilingue superposé + bon lien catégorie.
const PromoTile = ({ banner, fallback, title, categoryId, lang, testid }) => {
  const navigate = useNavigate();
  const { t } = useLang();
  const img = banner ? (lang === "en" ? (banner.image_en || banner.image_fr) : (banner.image_fr || banner.image_en)) : fallback;
  const go = () => {
    const link = banner?.link_url;
    if (link) {
      if (banner.open_new_window || /^https?:\/\//i.test(link)) window.open(link, banner.open_new_window ? "_blank" : "_self");
      else navigate(link);
    } else {
      navigate(`/shop?category=${categoryId}`);
    }
  };
  return (
    <button onClick={go} data-testid={testid}
      className="group relative flex min-h-[220px] items-stretch overflow-hidden rounded-lg border border-border text-left transition-shadow duration-200 hover:shadow-xl">
      <img src={img} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
        onError={(e) => { e.currentTarget.style.display = "none"; }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#2e3a47]/85 via-[#2e3a47]/55 to-transparent" />
      <div className="relative z-10 flex max-w-[70%] flex-col justify-center p-7">
        <h3 className="mb-4 font-display text-2xl font-black uppercase leading-tight tracking-tight text-white sm:text-3xl">{title}</h3>
        <span className="inline-flex w-fit items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-white transition-transform duration-200 group-hover:translate-x-1">
          {t.viewCategory} <ArrowRight size={15} weight="bold" />
        </span>
      </div>
    </button>
  );
};

const ProductCarousel = ({ title, items }) => {
  const { t, lang } = useLang();
  const { add } = useCart();
  const navigate = useNavigate();
  const ref = useRef(null);
  const scroll = (dir) => ref.current?.scrollBy({ left: dir * 560, behavior: "smooth" });
  return (
    <section className="mx-auto max-w-[1280px] px-4 py-8">
      <div className="mb-5 flex items-center justify-between border-b-2 border-primary pb-2">
        <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-[#2e3a47]" data-testid="section-title">{title}</h2>
        <div className="flex items-center gap-2">
          <button onClick={() => scroll(-1)} className="rounded border border-border p-1.5 text-[#2e3a47] hover:bg-muted"><CaretLeft size={16} weight="bold" /></button>
          <button onClick={() => scroll(1)} className="rounded border border-border p-1.5 text-[#2e3a47] hover:bg-muted"><CaretRight size={16} weight="bold" /></button>
          <button onClick={() => navigate("/shop")} className="ml-2 flex items-center gap-1 text-sm font-semibold text-[#0388cc] hover:underline">{t.allProducts} <ArrowRight size={14} weight="bold" /></button>
        </div>
      </div>
      <div ref={ref} className="flex gap-4 overflow-x-auto scrollbar-hide pb-2" style={{ scrollSnapType: "x mandatory" }}>
        {items.map((p) => (
          <div key={p.product_id} data-testid={`home-product-${p.product_id}`} className="group flex w-44 shrink-0 flex-col rounded border border-border bg-white p-3 transition hover:shadow-lg" style={{ scrollSnapAlign: "start" }}>
            <button onClick={() => navigate(`/product/${p.product_id}`)} className="mb-2 aspect-square overflow-hidden bg-white">
              {p.image ? <img src={p.image} alt="" loading="lazy" className="h-full w-full object-contain transition-transform duration-200 group-hover:scale-105" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <div className="flex h-full items-center justify-center text-muted-foreground"><Package size={32} /></div>}
            </button>
            <div className="font-mono text-[10px] text-primary">{p.product_code}</div>
            <button onClick={() => navigate(`/product/${p.product_id}`)} className="mt-0.5 mb-3 line-clamp-2 min-h-[2rem] text-left text-xs font-medium leading-tight text-[#2e3a47] hover:text-[#0388cc]">{p.name[lang] || p.name.fr}</button>
            <button onClick={() => { add(p, 1); toast.success(t.addedToCart); }} className="mt-auto flex items-center justify-center gap-1 rounded bg-primary py-1.5 text-[11px] font-bold uppercase text-white hover:brightness-95 transition"><Plus size={12} weight="bold" /> {t.addToCart}</button>
          </div>
        ))}
      </div>
    </section>
  );
};

export default function HomePage() {
  const { t, lang } = useLang();
  const [banners, setBanners] = useState([]);
  const [sections, setSections] = useState({ new: null, featured: null });
  const [brands, setBrands] = useState([]);

  useEffect(() => {
    axios.get(`${API}/shop/banners`).then((r) => setBanners(r.data.banners || [])).catch(() => {});
    axios.get(`${API}/shop/home-sections`).then((r) => setSections(r.data || {})).catch(() => {});
    axios.get(`${API}/shop/brands`).then((r) => setBrands(r.data || [])).catch(() => {});
  }, []);

  const carouselBanners = banners.filter((b) => (b.location || "carousel") === "carousel");
  const midLeft = banners.find((b) => b.location === "middle_left");
  const midRight = banners.find((b) => b.location === "middle_right");
  const sTitle = (s, fb) => (s ? (lang === "en" ? (s.title_en || s.title_fr) : (s.title_fr || s.title_en)) : fb) || fb;

  return (
    <div className="min-h-screen bg-[#f4f7f9]">
      <ShopHeader />

      <div className="mx-auto max-w-[1280px] px-4 py-4">
        <div className="flex gap-4">
          <aside className="hidden w-72 shrink-0 lg:block"><CategorySidebar /></aside>
          <div className="relative flex min-w-0 flex-1 flex-col">
            <TopNav />
            <Carousel banners={carouselBanners} lang={lang} />
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 pb-2">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="promo-banners">
          <PromoTile banner={midLeft} fallback={PROMO_LEFT} title={t.paperSystemTitle} categoryId="900" lang={lang} testid="promo-paper" />
          <PromoTile banner={midRight} fallback={PROMO_RIGHT} title={t.handSystemTitle} categoryId="920" lang={lang} testid="promo-hand" />
        </div>
      </div>

      {sections.new?.products?.length > 0 && <ProductCarousel title={sTitle(sections.new, t.newProducts)} items={sections.new.products} />}
      {sections.featured?.products?.length > 0 && <ProductCarousel title={sTitle(sections.featured, t.featuredProducts)} items={sections.featured.products} />}

      <section className="mx-auto max-w-[1280px] px-4 py-8">
        <div className="mb-5 border-b-2 border-primary pb-2">
          <h2 className="font-display text-2xl font-bold uppercase tracking-wide text-[#2e3a47]">{t.brands}</h2>
        </div>
        <div className="grid grid-cols-3 items-center gap-6 sm:grid-cols-4 md:grid-cols-6">
          {(brands.length ? brands.map((b) => ({ key: b.id, src: b.logo, alt: b.name })) : BRANDS.map((u) => ({ key: u, src: u, alt: "" }))).map((b) => (
            <div key={b.key} className="flex h-16 items-center justify-center rounded border border-border bg-white p-2">
              <img src={b.src} alt={b.alt} title={b.alt} className="max-h-full max-w-full object-contain grayscale transition hover:grayscale-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            </div>
          ))}
        </div>
      </section>

      <ShopFooter />
    </div>
  );
}
